import { useMutation, useSubscription } from "@apollo/client";
import {
  CREATE_USER_PHOTO,
  GENERATE_USER_UPLOAD_URL,
  UPDATE_ME,
} from "@hey-lady/gql/mutations/user";
import { ME, MY_PROFILE } from "@hey-lady/gql/queries/user";
import {
  USER_PERMISSIONS_UPDATED,
  USER_STATISTICS_UPDATED,
} from "@hey-lady/gql/subscriptions/user";
import { noop } from "@hey-lady/shared/helpers/utils";
import {
  type UpdateUserInput,
  UserBadgeType,
  type UserModel,
  useMeQuery,
} from "@hey-lady/shared/types/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { differenceInMinutes } from "date-fns";
import { DefaultValues, useForm } from "react-hook-form";
import type * as yup from "yup";
import { birthdayToUtcMidnight } from "../helpers/birthday";
import { LOCK_FILTERS_RELEASE_DATE } from "../helpers/const";
import { upload } from "../helpers/file";
import { useUploadFile } from "./uploadFile";
import { LOGIN_STORAGE_KEY } from "./useAuthUserTracking";

interface DefaultUpdateUserValues extends UpdateUserInput {
  location?: {
    city?: string | null | undefined;
    country?: string | null | undefined;
    cityId?: string | null | undefined;
    countryId?: string | null | undefined;
    lat?: number | null | undefined;
    long?: number | null | undefined;
  };
}

export const useAuth = () => {
  const { data, loading, error, refetch, updateQuery } = useMeQuery({
    onCompleted: (data) => {
      window.localStorage.setItem(LOGIN_STORAGE_KEY, JSON.stringify(data?.me.id));
    },
  });

  useSubscription(USER_STATISTICS_UPDATED, {
    skip: !data?.me?.id, // Skip subscription if user is not authenticated
    onData: ({ data: subscriptionData }) => {
      const update = subscriptionData.data?.userStatisticsUpdated;
      if (update?.eventStatistics) {
        updateQuery((prevData) => {
          if (prevData?.me) {
            return {
              ...prevData,
              me: {
                ...prevData.me,
                eventStatistics: update.eventStatistics,
              },
            };
          }
          return prevData;
        });
      }
    },
  });

  useSubscription(USER_PERMISSIONS_UPDATED, {
    skip: !data?.me?.id,
    onData: ({ data: subscriptionData }) => {
      const update = subscriptionData.data?.userPermissionsUpdated;
      if (update?.userId) {
        void refetch();
      }
    },
  });

  return {
    me: data?.me as UserModel,
    authenticated: !!data?.me,
    error,
    loading,
    refetch,
  } as const;
};

export const userProfileInput = (schema: DefaultUpdateUserValues) => {
  return {
    avatarUrl: schema?.avatarUrl,
    story: schema?.story,
    city: schema?.location?.city,
    country: schema?.location?.country,
    cityId: schema?.location?.cityId,
    countryId: schema?.location?.countryId,
    lat: schema?.location?.lat,
    long: schema?.location?.long,
    firstName: schema?.firstName,
    lastName: schema?.lastName,
    birthday: schema?.birthday ? birthdayToUtcMidnight(schema.birthday) : schema?.birthday,
    showBirthday: schema?.showBirthday,
    showBirthdayYear: schema?.showBirthdayYear,
    needSpeakingPartner: schema?.needSpeakingPartner,
    nativeLanguage: schema?.nativeLanguage,
    otherLanguage: schema?.otherLanguage,
    englishSkills: schema?.englishSkills,
    interests: schema?.interests,
    occupationId: schema?.occupationId,
    nationality: schema?.nationality,
    timezone: schema?.timezone,
  };
};

export const useSetupProfile = () => {
  const [updateMe, { loading, error }] = useMutation(UPDATE_ME, { refetchQueries: [ME] });

  const update = async (data: DefaultUpdateUserValues): Promise<void> => {
    const variables = {
      input: userProfileInput(data),
    };
    try {
      await updateMe({ variables });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    update,
    loading,
    error,
  } as const;
};

export const useUpdateProfile = <TFormValues extends Record<string, any>>({
  schema,
  defaultValues,
  onAfterUpdate = noop,
}: {
  schema: yup.ObjectSchema<TFormValues>;
  defaultValues: DefaultValues<TFormValues>;
  onAfterUpdate: () => void;
}) => {
  const [updateMe, { loading, error }] = useMutation(UPDATE_ME, {
    refetchQueries: [{ query: ME }],
  });

  const { register, reset, control, getValues, formState, trigger, setValue } =
    useForm<TFormValues>({
      mode: "all",
      defaultValues,
      // v6 types yupResolver as Resolver<Yup.InferType<schema>>; yup's MakeKeysOptional
      // mapping is unrelated to a generic TFormValues in both Resolver parameter and
      // return positions, so no assertion bridges it. Explicit type args relax only the
      // resolver's self-inference; the schema parameter still asserts TFormValues.
      resolver: yupResolver<any, any, any>(schema),
    });

  const update = async (): Promise<void> => {
    const variables = {
      input: userProfileInput(getValues()),
    };

    try {
      await updateMe({ variables });
      onAfterUpdate();
    } catch (_error) {}
  };

  return {
    loading,
    error,
    control,
    register,
    formState,
    trigger,
    update,
    reset,
    setValue,
  } as const;
};

export const useUpdateProfilePhoto = (onChange?: (avatarUrl: string) => void) => {
  const [updateMe] = useMutation(UPDATE_ME);
  const [generateUserUploadUrl] = useMutation(GENERATE_USER_UPLOAD_URL);

  const { loading, onDrop } = useUploadFile(async ([file]: File[]): Promise<void> => {
    const variables = {
      input: {
        filename: file.name,
      },
    };

    const { data } = await generateUserUploadUrl({ variables });
    await upload(data?.generateUserUploadUrl?.uploadURL, file);

    if (onChange) {
      onChange(data?.generateUserUploadUrl?.contentURL);
    }
    await updateMe({
      variables: { input: { avatarUrl: data?.generateUserUploadUrl?.contentURL } },
    });
  });

  return {
    loading,
    onDrop,
  } as const;
};

export const useUploadAttachmentPhoto = () => {
  const [generateUserUploadUrl] = useMutation(GENERATE_USER_UPLOAD_URL);
  const [createUserPhoto] = useMutation(CREATE_USER_PHOTO, {
    refetchQueries: [{ query: MY_PROFILE }],
  });
  const { loading, onDrop } = useUploadFile(async ([file]: File[]): Promise<void> => {
    const variables = {
      input: {
        filename: file.name,
      },
    };
    const { data } = await generateUserUploadUrl({ variables });
    await upload(data?.generateUserUploadUrl?.uploadURL, file);
    await createUserPhoto({
      variables: { input: { url: data?.generateUserUploadUrl?.contentURL } },
    });
  });

  return {
    loading,
    onDrop,
  } as const;
};

export const useIsUserOnline = (lastOnlineAt: Date | number | null) => {
  // TODO change to a more accurate approach (?)
  // Consider users online if their lastOnlineAt is less than 3 minutes due to saving lastOnlineAt job latency
  return lastOnlineAt ? differenceInMinutes(new Date(), lastOnlineAt) < 3 : false;
};

const MIN_EVENTS_TO_HOST = 3;

export const useCanCreate = (user: UserModel) => {
  const attendedEvents = user?.eventStatistics?.totalAttendedScheduledEvents ?? 0;
  const createdAt = user?.createdAt ? new Date(user.createdAt as unknown as string | number) : null;
  const isPostReleaseUser = createdAt ? createdAt > LOCK_FILTERS_RELEASE_DATE : false;
  const meetsAttendanceRequirement = !isPostReleaseUser || attendedEvents >= MIN_EVENTS_TO_HOST;

  return user?.isVerified && user?.stepsCompleted && meetsAttendanceRequirement;
};

export const useHasBadge = (user: UserModel, badgeType: UserBadgeType) => {
  return user?.badges?.some((badge) => badge.type === badgeType) ?? false;
};
